import React, { useState, useEffect, useMemo } from "react";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Tag, Check, X, Truck } from "lucide-react";

/**
 * PayPal payment buttons wrapper with optional discount-code redemption (merch only).
 *
 * Props:
 *  - type: "merch" | "subscription"
 *  - payload: object posted to backend create-order endpoint
 *  - subtotal: number — required for merch to drive the discount preview
 *  - disabled: bool
 *  - onSuccess: function(captureResponse) called after successful capture
 */
export default function PayPalCheckout({ type, payload, subtotal, initialCode, disabled, onSuccess }) {
  const { user } = useAuth();
  const isMerch = type === "merch";
  const [codeInput, setCodeInput] = useState(initialCode || "");
  const [applied, setApplied] = useState(null); // {code, percent_off, discount_amount, total}
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState("");
  const [availableCodes, setAvailableCodes] = useState([]);
  const [autoTried, setAutoTried] = useState(false);
  const [promo, setPromo] = useState(null);  // {active, remaining, discount}

  // Pull free-shipping promo state so we can preview the auto-applied $6 off
  useEffect(() => {
    if (!isMerch) return;
    api.get("/promo/free-shipping")
      .then((r) => setPromo(r.data))
      .catch(() => setPromo(null));
  }, [isMerch]);

  // Load this user's unlocked, still-redeemable codes so we can prompt them
  useEffect(() => {
    if (!isMerch || !user) { setAvailableCodes([]); return; }
    api.get("/discount/codes")
      .then((r) => {
        const open = (r.data?.codes || []).filter((c) => !c.used);
        setAvailableCodes(open);
      })
      .catch(() => setAvailableCodes([]));
  }, [isMerch, user]);

  // Reset applied discount when the underlying cart changes
  useEffect(() => {
    setApplied(null);
    setError("");
    setAutoTried(false);
  }, [payload?.product_id, payload?.variant_id, payload?.quantity, subtotal]);

  const finalPayload = useMemo(() => {
    if (!isMerch || !applied) return payload;
    return { ...payload, discount_code: applied.code };
  }, [isMerch, applied, payload]);

  // Auto-apply pre-staged code from query string (e.g., from streak milestone toast)
  useEffect(() => {
    if (!isMerch || !user || !initialCode || autoTried || applied || !subtotal) return;
    setAutoTried(true);
    applyCode(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMerch, user, initialCode, subtotal, autoTried, applied]);

  const applyCode = async (codeRaw) => {
    const code = (codeRaw || codeInput).trim().toUpperCase();
    if (!code) { setError("Enter a code first"); return; }
    if (!user) { setError("Sign in to use a discount code"); return; }
    setValidating(true);
    setError("");
    try {
      const r = await api.post("/discount/validate", {
        code,
        subtotal: Number(subtotal || 0),
      });
      if (r.data?.valid) {
        setApplied(r.data);
        setCodeInput(code);
        toast.success(`${code} applied — ${r.data.percent_off}% off`);
      } else {
        setApplied(null);
        setError(r.data?.error || "Code not valid");
      }
    } catch (e) {
      setApplied(null);
      setError(e?.response?.data?.detail || "Could not validate code");
    } finally {
      setValidating(false);
    }
  };

  const removeCode = () => {
    setApplied(null);
    setCodeInput("");
    setError("");
  };

  const createOrder = async () => {
    const endpoint = type === "subscription"
      ? "/paypal/subscription/create-order"
      : "/paypal/create-order";
    try {
      const r = await api.post(endpoint, finalPayload);
      return r.data.order_id;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start PayPal checkout");
      throw e;
    }
  };

  const onApprove = async (data) => {
    try {
      const r = await api.post(`/paypal/capture-order/${data.orderID}`);
      onSuccess?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment capture failed");
    }
  };

  const onError = (err) => {
    console.error("PayPal error", err);
    toast.error("PayPal hit a snag. Try again.");
  };

  const onCancel = () => {
    toast.info("Payment cancelled.");
  };

  return (
    <div className={disabled ? "opacity-50 pointer-events-none" : ""} data-testid={`paypal-buttons-${type}`}>
      {isMerch && user && (
        <div className="mb-4" data-testid="discount-section">
          {!applied ? (
            <>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D9688]" />
                  <input
                    type="text"
                    data-testid="discount-code-input"
                    value={codeInput}
                    onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && applyCode()}
                    placeholder="DISCOUNT CODE"
                    className="w-full pl-9 pr-3 py-2 bg-[#0A0E0B] border-2 border-[#2C332A] text-[#E5E5E5] font-anton uppercase tracking-widest text-sm focus:border-[#DD5422] outline-none"
                  />
                </div>
                <button
                  type="button"
                  data-testid="discount-apply-btn"
                  disabled={validating || !codeInput.trim()}
                  onClick={() => applyCode()}
                  className="px-4 py-2 bg-[#DD5422] text-[#0A0E0B] font-anton uppercase tracking-widest text-xs hover:bg-[#FF6B35] disabled:opacity-50 border-2 border-[#DD5422]"
                >
                  {validating ? "..." : "Apply"}
                </button>
              </div>
              {availableCodes.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid="available-codes">
                  <span className="text-[10px] uppercase tracking-widest text-[#8D9688] mr-1 self-center">Available:</span>
                  {availableCodes.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      data-testid={`apply-${c.code}`}
                      onClick={() => applyCode(c.code)}
                      className="px-2 py-1 border border-[#DD5422] text-[#DD5422] font-anton text-[10px] uppercase tracking-widest hover:bg-[#DD5422] hover:text-[#0A0E0B] transition-colors"
                    >
                      {c.code} ({c.percent_off}%)
                    </button>
                  ))}
                </div>
              )}
              {error && (
                <div className="text-xs text-red-400 mt-2 uppercase tracking-widest" data-testid="discount-error">
                  {error}
                </div>
              )}
            </>
          ) : (
            <div className="border-2 border-[#4CAF50] bg-[#4CAF50]/10 p-3" data-testid="discount-applied">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#4CAF50]" />
                  <div>
                    <div className="font-anton uppercase tracking-widest text-sm text-[#4CAF50]">
                      {applied.code} — {applied.percent_off}% off
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[#8D9688]">
                      Save ${applied.discount_amount.toFixed(2)} · new total ${applied.total.toFixed(2)}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="discount-remove-btn"
                  onClick={removeCode}
                  className="text-[#8D9688] hover:text-[#DD5422] p-1"
                  title="Remove code"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isMerch && applied && (
        <div className="mb-2 flex justify-between items-center text-xs" data-testid="discount-summary">
          <span className="uppercase tracking-widest text-[#8D9688]">Final total</span>
          <span className="font-anton text-xl text-[#DD5422]">${applied.total.toFixed(2)}</span>
        </div>
      )}

      {isMerch && !applied && promo && promo.active && Number(subtotal || 0) > promo.discount && (
        <div className="mb-3 border-2 border-[#DD5422] bg-[#DD5422]/10 p-3 flex items-center gap-3" data-testid="promo-free-shipping">
          <Truck className="w-5 h-5 text-[#DD5422] shrink-0" strokeWidth={2.5} />
          <div className="flex-1">
            <div className="font-anton uppercase tracking-widest text-sm text-[#DD5422]">
              First 50 ship free — ${promo.discount.toFixed(0)} off auto-applied
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#8D9688]">
              New total ${Math.max(0.01, Number(subtotal) - promo.discount).toFixed(2)} · only {promo.remaining} spot{promo.remaining === 1 ? "" : "s"} left
            </div>
          </div>
        </div>
      )}

      <PayPalButtons
        style={{
          layout: "vertical",
          shape: "rect",
          color: "gold",
          label: "paypal",
          height: 48,
        }}
        disabled={disabled}
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onError}
        onCancel={onCancel}
        forceReRender={[JSON.stringify(finalPayload), type]}
      />
    </div>
  );
}
